import { staticData } from "../constants";

export function getOptions(answers) {
  const { type, sqFt, province, sqFtRef } = answers.reduce((acc, answer) => {
    const key = answer.field.ref;

    if (key === "heating_type") {
      acc.type =
        answer.choice.label === "Forced air furnace or central heat pump"
          ? "Central"
          : "Ductless";
    }

    if (key === "sq_ft-Central" || key === "sq_ft-Ductless") {
      acc.sqFt = answer.choice.label;
    }

    if (key.startsWith("utility_")) {
      acc.province = key.split("_")[1].toUpperCase();
    }

    if (key === "sq_ft-Central" || key === "sq_ft-Ductless") {
      acc.sqFt = answer.choice.label;
      acc.sqFtRef = key;
    }

    return acc;
  }, {});

  if (!answers.length) {
    return [];
  }

  if (!type) {
    console.error("No type found in answers");
    return [];
  }

  if (!sqFt) {
    console.error("No sqFt found in answers");
    return [];
  }

  if (!province) {
    console.error("No province found in answers");
    return [];
  }

  const staticDataByType = staticData[type];

  const heatingSystemOptions =
    type === "Central"
      ? staticDataByType[sqFtRef][sqFt]
      : staticData.Ductless.system;

  const installationBase = type === "Central" ? staticDataByType.installation_base : null;

  if (!heatingSystemOptions && type === "Central") {
    console.error("No heating system options found for sqFt: ", sqFt);
    return [];
  }

  if (!heatingSystemOptions && type === "Ductless") {
    console.error("No heating system options found for ducltess ");
    return [];
  }

  const options = heatingSystemOptions.options
    .map((option, index) => {
      const multiplier = heatingSystemOptions.multiplier[province];
      const grantAllowance = staticData.additional.grant_maximums[province];

      // Build all answer items first, then we'll sort them
      const answerItems = answers.reduce((acc, answer) => {
        const key = answer.field.ref;

        // skip sq_ft as we don't need it anymore, as it is heatingSystemOptions
        if (key === sqFtRef) {
          return acc;
        }

        const value = answer.choice
          ? answer.choice.label
          : answer.text || answer.boolean;

        const option = staticDataByType[key]?.[value]?.options?.[index];

        const multiplier =
          staticDataByType[key]?.[value]?.multiplier?.[province];

        if (!option) {
          return acc;
        }

        return [
          ...acc,
          {
            ...option,
            multiplier,
            price: option.price * multiplier || 0,
            fieldKey: key, // Add this to track which field it came from
          },
        ];
      }, []);

      // Separate items by category for Ductless
      let headItems = [];
      let installationPackageItems = [];
      let otherItems = [];

      if (type === "Ductless") {
        answerItems.forEach(item => {
          if (item.fieldKey && item.fieldKey.startsWith("rooms_")) {
            // These are head items
            headItems.push(item);
          } else if (item.fieldKey === "room_number") {
            // This is the installation package
            installationPackageItems.push(item);
          } else {
            // Everything else
            otherItems.push(item);
          }
        });
      }

      // Build items array based on type
      let items;
      
      if (type === "Central") {
        // Central order: 1) main system 2) installation package 3) everything else
        items = [
          { ...option, multiplier, price: option.price * multiplier || 0 },
          ...(installationBase ? [{
            ...installationBase.options[index],
            multiplier: installationBase.multiplier[province],
            price: installationBase.options[index].price * installationBase.multiplier[province]
          }] : []),
          ...answerItems,
        ];
      } else {
        // Ductless order: 1) main system (placeholder) 2) heads 3) installation package 4) everything else
        // Note: condenser gets added later in the next .map()
        items = [
          { ...option, multiplier, price: option.price * multiplier || 0 },
          ...headItems,
          ...installationPackageItems,
          ...otherItems,
        ];
      }

      return {
        rank: option.option,
        total: 0,
        totalWarranty: 0,
        grantAllowance,
        type,
        province,
        title: getTitle({ ...option, type }),
        items,
      };
    })
    .map((option, index) => {
      if (option.type === "Central") {
        return option;
      }

      const maxBtu = 42000;
      const errorBtu = 45000;
      let totalBtu = getTotalBtu(option);

      if (totalBtu > errorBtu) {
        throw new Error("Total Btu is too high");
      }

      if (totalBtu > maxBtu) {
        totalBtu = maxBtu;
      } else {
        const conderserSums = Object.keys(
          staticDataByType.condenser_btu_sum
        ).map((key) => parseInt(key));

        totalBtu = conderserSums.find((sum) => sum > totalBtu);
      }

      const condenser =
        staticDataByType.condenser_btu_sum?.[`${totalBtu}`]?.options?.[index];

      if (!condenser) {
        return option;
      }

      const condenserMultiplier =
        staticDataByType.condenser_btu_sum[`${totalBtu}`].multiplier[province];

      // For Ductless: insert condenser at the beginning (after placeholder system)
      const condenserItem = {
        ...condenser,
        price: condenser.price * condenserMultiplier || 0,
      };

      // Remove the placeholder system item and add condenser as first item
      const itemsWithoutPlaceholder = option.items.slice(1);

      return {
        ...option,
        items: [
          condenserItem,
          ...itemsWithoutPlaceholder,
        ],
      };
    })
    .map((option) => {
      return {
        ...option,
        addons: staticData["additional"]["addons"][type].options || [],
        image: staticData["heat-pump-images"][option.rank][type],
        highlights: staticData["product-highlights"][option.rank][type],
        total: option.items.reduce((acc, item) => acc + item.price || 0, 0),
        totalAfterGrant:
          option.items.reduce((acc, item) => acc + item.price || 0, 0) -
          option.grantAllowance,
        totalWarranty: option.items.reduce(
          (acc, item) => acc + item.warranty_price || 0,
          0
        ),
      };
    });

  return options;
}

const getTotalBtu = (option) => {
  return option.items.reduce((acc, item) => acc + item.btu || 0, 0);
};

const getTitle = (option) => {
  const systemBrandName = option.title.split(" ")[0];
  const systemType = option.type === "Central" ? "Ducted" : "Ductless";
  return `${systemBrandName} ${systemType} System with Installation`;
};
